import SourceAssessmentEvidence from './SourceAssessmentEvidence';
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  isOrderType,
  supportsAnswerCarryover,
} from '../../constants/visitTypes';
import { OrderDetailPanel } from '../order/OrderDetailPanel';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  FileText,
  Lock,
  LockOpen,
} from 'lucide-react';

import { getUserFacingApiMessage } from '../../utils/apiErrorDetail';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { canEditChartContent } from '../../utils/chartEditPermissions';
import {
  getPatientDocumentUrl,
  getVisit,
  updateVisitData,
  breakTaskSignatures,
  getTaskSignatures,
  updateMissedVisitData,
} from '../../services/api';
import { validateOasisVisitsVisitIdValidateOasisPost } from '../../services/api/sdk.gen';
import type {
  ClinicPhysicianResponse,
  PatientResponse,
  VisitResponse,
  TaskSignatureResponse,
  MissedVisitDataUpdate,
  OrderResponse,
} from '../../services/api/types.gen';
import {
  MissedVisitForm,
  MISSED_VISIT_AUTOSAVE_DEBOUNCE_MS,
} from './MissedVisitForm';
import { Button, ConfirmationDialog } from '@roger/ui-components';
import {
  OASISForm,
  OASISFormData,
  ReadonlyChartView,
  getOasisAutosaveDebounceMs,
} from '../oasis';
import { AutoSavePill } from '../AutoSavePill';
import { SaveDeclinedError } from '../../hooks/useAutoSave';
import type { AutoSaveStatus } from '../../hooks/useAutoSave';
import { SignatureHistoryDialog } from '../signing/SignatureHistoryDialog';
import { WorkLogDialog } from '../task/WorkLogDialog';
import { EvvReadingsDialog } from './EvvReadingsDialog';
import { useActiveAttestations } from '../signing/useActiveAttestations';
import { useTaskSignatureBypasses } from '../signing/useTaskSignatureBypasses';
import { useAllowSubmitWithoutSignature } from '../../hooks/useAllowSubmitWithoutSignature';
import { useOasisSigningRequirements } from '../../hooks/useOasisSigningRequirements';
import { OasisFatalErrorsDialog } from '../signing/OasisFatalErrorsDialog';
import { PermissionSlug } from '../../constants/featureFlags';
import { useStore } from '../../store';
import { useToast } from '../../hooks/useToastContext';
import {
  getSignButtonLabel,
  useTaskPermissions,
} from '../task/TaskDetailPanel';
import { EDITABLE_STATUSES } from '../../constants/statuses';
import {
  closedEpisodeCopy,
  useClosedEpisodeLock,
} from '../../utils/closedEpisodeLock';
import { VisitDetailHeader } from './VisitDetailHeader';
import { VisitSigningDialogs } from './VisitSigningDialogs';
import { VisitTaskNotesDialog } from './VisitTaskNotesDialog';
import { MarkVisitMissedDialog } from './MarkVisitMissedDialog';
import { WAIVE_EVAL_ORDER_CONFIRM } from './waiveEvalOrderCopy';
import { ChangeVisitTypeDialog } from './ChangeVisitTypeDialog';
import { VisitTransitionNotesDialog } from './VisitTransitionNotesDialog';
import { useVisitDetailActions } from './useVisitDetailActions';

/**
 * The `visit_data` keys the Kinnser importer *derives* rather than scrapes: an
 * ordinary visit's charted clock times, and the OASIS shell's completion date,
 * patient-tracking clock times and attending NPI. None of them is an answer
 * anyone gave, so a chart holding nothing else is still an empty chart — an
 * imported assessment included, since the shell always carries them.
 *
 * Source of truth: `_IMPORTER_DERIVED_VISIT_DATA_KEYS` in `backend/app/api/visits.py`,
 * which imports the qids by name from the importer. Keep the two lists together.
 */
const IMPORTER_DERIVED_KEYS = [
  'time_in',
  'time_out',
  'd_completed_9665b2',
  't_necessary_cd1b4b',
  't_necessary_f950fa',
  't_physician_npi_m0018',
  // The importer places the admission's discharge disposition where billing
  // reads FL17 from — claim plumbing, not documentation that came across.
  'r_status_0167b9',
];

/**
 * How far after `imported_at` a `visit_data_last_updated` stamp is still the
 * importer's own, in milliseconds.
 *
 * New imports stamp both columns with one value, so for them an exact
 * comparison would do. Charts imported before that change carry a stamp taken
 * partway into the run — seconds after `imported_at` — and the grace exists
 * for those legacy rows only. Two minutes covers any legacy run's write phase
 * while real clinician edits, minutes to days later, correctly read as
 * Roger's. Mirrors `IMPORT_STAMP_GRACE` in `backend/app/services/kinnser_import/ownership.py`.
 */
const IMPORT_STAMP_GRACE_MS = 2 * 60 * 1000;

/**
 * What the imported-chart signpost can truthfully say about the chart below it.
 *
 * `imported_at` marks provenance and never clears, so it answers "where did this
 * visit come from" and nothing else. Four different charts carry that stamp:
 *
 * - `documented` — the importer wrote these answers and nobody has since. An
 *   imported OASIS assessment gets Kinnser's scraped answers, so the panel
 *   renders a populated chart; a banner calling it empty contradicts what the
 *   clinician is reading.
 * - `edited` — the chart has been saved in Roger since the import, so its
 *   answers are not Kinnser's to claim. A chart carrying both scraped answers
 *   and later Roger charting lands here too: the provenance line stays, and the
 *   second line claims only what is certainly true, so neither party is credited
 *   with the other's work.
 * - `open` — nothing came across and the visit is still editable. A task Kinnser
 *   had scheduled but nobody performed imports as a Not Started visit dated from
 *   its target date, and Roger is where it gets documented. Telling that
 *   clinician the record lives in Kinnser reads as "do not chart this here".
 * - `empty` — a finished visit whose documentation never became Roger answers.
 *   The original case: the chart is blank by design and the record is elsewhere.
 */
type ImportedChartState = 'documented' | 'edited' | 'open' | 'empty';

/** True when someone saved this chart in Roger after the import wrote it. */
function editedSinceImport(visit: VisitResponse): boolean {
  if (!visit.imported_at || !visit.visit_data_last_updated) return false;
  const importedAt = Date.parse(visit.imported_at);
  const lastUpdated = Date.parse(visit.visit_data_last_updated);
  if (Number.isNaN(importedAt) || Number.isNaN(lastUpdated)) return false;
  return lastUpdated > importedAt + IMPORT_STAMP_GRACE_MS;
}

function importedChartState(
  visit: VisitResponse,
  isEditable: boolean
): ImportedChartState {
  const answered = Object.keys(visit.visit_data ?? {}).some(
    key => !IMPORTER_DERIVED_KEYS.includes(key)
  );
  if (answered) return editedSinceImport(visit) ? 'edited' : 'documented';
  return isEditable ? 'open' : 'empty';
}

/** The signpost's two lines for a chart state, with and without a print view. */
function importedSignpostCopy(
  state: ImportedChartState,
  hasPrintView: boolean
): { headline: string; detail: string } {
  if (state === 'documented') {
    return {
      headline: 'Documentation was imported from an External EMR.',
      detail: 'The answers the External EMR held are in the chart below.',
    };
  }
  if (state === 'edited') {
    return {
      headline: 'This visit was imported from an External EMR.',
      detail: 'This chart holds documentation added in Roger since the import.',
    };
  }
  if (state === 'open') {
    return {
      headline: 'This visit was imported from an External EMR.',
      detail: hasPrintView
        ? 'No documentation came across. The External EMR print view holds what the External EMR had.'
        : 'No documentation came across. Document this visit in Roger.',
    };
  }
  return {
    headline: 'Documentation was imported from an External EMR.',
    detail: hasPrintView
      ? 'This chart is empty. The External EMR print view holds the record.'
      : 'This chart is empty. No External EMR print view was captured; the record remains in the External EMR.',
  };
}

interface VisitDetailPanelProps {
  visitId: string;
  patientId: string;
  /** Full patient record; forwarded to the OASIS form's embedded allergies module. */
  patient?: PatientResponse | null;
  onBack?: () => void;
  onVisitUpdated?: (visit: VisitResponse) => void;
  physicians?: ClinicPhysicianResponse[];
  /**
   * This visit is soft-deleted, opened from the deleted-tasks view. Widens the
   * reads that would otherwise 404 and makes the chart strictly read only: no
   * form, no autosave, no signing, no unlock. The Actions menu keeps only print
   * view, signature history and work log.
   */
  isDeleted?: boolean;
}

export const VisitDetailPanel: React.FC<VisitDetailPanelProps> = ({
  visitId,
  patientId,
  patient,
  onBack,
  onVisitUpdated,
  physicians = [],
  isDeleted = false,
}) => {
  const navigate = useNavigate();
  const user = useStore(state => state.user);
  const hasPermission = useStore(state => state.hasPermission);
  const canViewWorkLog = hasPermission(PermissionSlug.WORKLOG_VIEW);
  const canWaiveEvalOrder = hasPermission(PermissionSlug.ORDERS_WAIVE_EVAL);
  const canViewEvv = useStore(state =>
    state.hasPermission(PermissionSlug.EVV_VIEW)
  );
  const canRemoveEvv = useStore(state =>
    state.hasPermission(PermissionSlug.EVV_REMOVE)
  );
  const { error: showError } = useToast();
  const allowUnsignedMoves = useAllowSubmitWithoutSignature();
  const oasisSigningRequirements = useOasisSigningRequirements();
  // Null until the Live Scrubber has validated once.
  const [oasisFatalCount, setOasisFatalCount] = useState<number | null>(null);
  // The locked-chart check below failed, so no count is coming.
  const [lockedOasisCheckFailed, setLockedOasisCheckFailed] = useState(false);
  const [scrubberFocusRequest, setScrubberFocusRequest] = useState(0);
  const showOasisErrors = useCallback(
    () => setScrubberFocusRequest(n => n + 1),
    []
  );
  const {
    attestations,
    isLoading: attestationsLoading,
    error: attestationsError,
  } = useActiveAttestations();

  useEffect(() => {
    if (attestationsError) {
      showError(
        'Could not load signing text',
        'Refresh the page or try again in a moment.'
      );
    }
  }, [attestationsError, showError]);

  // Core visit state
  const [visit, setVisit] = useState<VisitResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Imported-chart signpost (EMR-1264): whatever the chart below holds, the
  // banner offers the Kinnser print view when one was captured.
  const [printViewOpening, setPrintViewOpening] = useState(false);
  const kinnserPrintViewDocumentId = visit?.kinnser_print_view_document_id;
  const handleOpenKinnserPrintView = useCallback(async () => {
    if (!kinnserPrintViewDocumentId) return;
    setPrintViewOpening(true);
    try {
      const response = await getPatientDocumentUrl({
        path: {
          patient_id: patientId,
          document_id: kinnserPrintViewDocumentId,
        },
      });
      const url = (response?.data as { url?: string } | undefined)?.url;
      if (!url) throw new Error('Document URL missing from response');
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      console.error('Error opening the External EMR print view:', err);
      showError(
        'Document Error',
        'Unable to open the External EMR print view. Please try again.'
      );
    } finally {
      setPrintViewOpening(false);
    }
  }, [kinnserPrintViewDocumentId, patientId, showError]);

  // Signature state.
  //
  // Bypasses are not held here. They are derived from the server through
  // `useTaskSignatureBypasses`, and every write to `signatures` re-asks for
  // them, because the two move together: reopening a chart invalidates its
  // signatures AND supersedes its forfeiture, in one server call
  // (`break_task_signatures`). Refreshing one without the other left this
  // panel holding a bypass whose `superseded_at` was still null, so the
  // summary rendered "Awaiting 2 signatures" beside "Clinician signature
  // bypassed" — and, once the clinician signed for real, a genuine signature
  // beside a forfeiture of that same signature.
  //
  // Hence the wrapper rather than a bump at each call site: this panel has ten
  // places that refresh signatures, and the bug was one of them being added
  // without a matching bypass refresh. Coupling them here costs one extra GET
  // on mount and makes the omission unrepresentable.
  const [signatures, setSignaturesState] = useState<TaskSignatureResponse[]>(
    []
  );
  const [signatureRefreshNonce, setSignatureRefreshNonce] = useState(0);
  const setSignatures = useCallback((next: TaskSignatureResponse[]) => {
    setSignaturesState(next);
    setSignatureRefreshNonce(nonce => nonce + 1);
  }, []);
  const signatureBypasses = useTaskSignatureBypasses(
    visitId,
    signatureRefreshNonce
  );
  const [invalidationLoading, setInvalidationLoading] = useState(false);

  // Signature invalidation dialog state (OASIS save / unlock — stays in panel)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState<{
    data: Record<string, unknown>;
    isFormSave: boolean;
  } | null>(null);
  const [formKey, setFormKey] = useState(0);
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('idle');
  const [autoSaveChangeToken, setAutoSaveChangeToken] = useState(0);
  /** Bumped on every missed-visit field edit so a finishing save knows whether newer edits are pending. */
  const missedEditSeqRef = useRef(0);
  const savePromiseRef = useRef<{
    resolve: () => void;
    reject: (reason?: unknown) => void;
  } | null>(null);
  const [isUnlockAction, setIsUnlockAction] = useState(false);

  // Task notes (seeded from fetch; actions hook owns dialog draft)
  const [taskNotes, setTaskNotes] = useState('');
  const [missedDataSaving, setMissedDataSaving] = useState(false);

  // Copied answers staged in the form are held out of autosave until the
  // clinician keeps or discards them (EMR-675). Signing acts on saved data, so
  // signing with the banner still up would drop them from the signed note —
  // block the action and let the banner, which sits directly above these
  // buttons, be the instruction.
  const [hasStagedAnswers, setHasStagedAnswers] = useState(false);
  useEffect(() => {
    setHasStagedAnswers(false);
  }, [visit?.id]);
  const stagedAnswersTooltip = hasStagedAnswers
    ? 'Keep or discard the copied answers first'
    : undefined;

  // --- Derived permission state ---

  // A deleted chart is never editable, whatever status it was deleted in.
  // Deriving isEditable from this (rather than only hiding controls) is what
  // keeps the OASIS form, its autosave and the missed-visit form off the page
  // entirely, so there is nothing left that could issue a write.
  // A closed certification period also locks the chart unless the user has the
  // amend-closed permission. Both checks are folded into isEditable so no form
  // or autosave path remains available for a denied chart.
  const canEditChart = visit
    ? canEditChartContent({
        assignedClinicianUserId: visit.assigned_clinician_user_id,
        currentUserId: user?.id,
        clinicId: visit.agency_clinic_id,
        clinicPermissions: user?.user_clinics,
        activeClinicIds: user?.active_clinic_ids,
        agencyPermissions: user?.permissions,
      })
    : false;
  const { locked: closedEpisodeLocked, canAmend: canAmendClosedEpisode } =
    useClosedEpisodeLock(visit?.episode_closed);
  const isEditable = visit
    ? !isDeleted &&
      EDITABLE_STATUSES.has(visit.status) &&
      canEditChart &&
      !closedEpisodeLocked
    : false;
  const isReadonly = !isEditable;
  const closedEpisodeCopyForVisit = visit?.episode_closed
    ? closedEpisodeCopy(canAmendClosedEpisode)
    : null;
  const isMissedVisit = Boolean(visit?.missed_visit_data);

  // Imported-chart signpost (EMR-1264). Null on a native visit; otherwise the
  // state that decides what the banner may claim about the chart below it.
  const importedState =
    visit && visit.imported_at ? importedChartState(visit, isEditable) : null;
  const importedSignpost = importedState
    ? importedSignpostCopy(importedState, Boolean(kinnserPrintViewDocumentId))
    : null;
  // One banner, not two: an imported visit in a closed period keeps its
  // signpost and gains the closed sentence on the same card.
  const importedCopy =
    importedSignpost && closedEpisodeCopyForVisit
      ? {
          headline: importedSignpost.headline,
          detail: `${importedSignpost.detail} ${closedEpisodeCopyForVisit.headline} ${closedEpisodeCopyForVisit.detail}`,
        }
      : importedSignpost;

  // Clear stale save status whenever the form identity changes: another
  // visit, a missed/normal mode flip, or a discard remount via formKey
  useEffect(() => {
    setAutoSaveStatus('idle');
    setAutoSaveChangeToken(0);
    setOasisFatalCount(null);
    setLockedOasisCheckFailed(false);
  }, [visitId, isMissedVisit, formKey]);

  // Role-based checks still control signing and approval. Chart-content editing
  // is separately scoped to the assigned clinician or the permission above.
  const {
    hasClinicianRole,
    hasCaseManagerRole,
    canSign: canSignTask,
    canApprove: canApproveTask,
    canBreakSignatures: canBreakTaskSignatures,
    signRequiresESignature,
    signTargetStatus,
    canBypassSignature: canBypassTaskSignature,
    canSubmitWithoutSignature: canSubmitTaskWithoutSignature,
    canReturnToClinician: canReturnTaskToClinician,
  } = useTaskPermissions(user, visit, {
    allowSubmitWithoutSignature: allowUnsignedMoves,
  });

  // A deleted chart forfeits every workflow action, regardless of role. Zeroed
  // here rather than at each use site so a new action added below inherits the
  // suppression instead of having to remember it.
  const canSign = !isDeleted && canSignTask;
  const canApprove = !isDeleted && canApproveTask;
  const canBreakSignatures =
    !isDeleted && canEditChart && canBreakTaskSignatures;
  const canBypassSignature = !isDeleted && canBypassTaskSignature;
  const canSubmitWithoutSignature = !isDeleted && canSubmitTaskWithoutSignature;
  const canReturnToClinician = !isDeleted && canReturnTaskToClinician;

  // A locked chart renders no Live Scrubber to report its fatal count, so ask
  // once for the count the signing guard will see. Both scrub the saved chart.
  const needsLockedOasisCheck =
    Boolean(visit?.is_cms_submittable) &&
    isReadonly &&
    !isMissedVisit &&
    (canSign || canApprove || canBypassSignature);
  const savedVisitData = visit?.visit_data;
  useEffect(() => {
    if (!needsLockedOasisCheck) return;
    let cancelled = false;
    const load = async () => {
      try {
        const response = await validateOasisVisitsVisitIdValidateOasisPost({
          path: { visit_id: visitId },
          body: { answers: (savedVisitData ?? {}) as Record<string, unknown> },
          throwOnError: true,
        });
        if (cancelled || !response.data) return;
        setOasisFatalCount(
          response.data.errors.filter(err => err.severity === 'fatal').length
        );
      } catch (err) {
        // Unknown count: the backend still refuses, and that opens the dialog.
        console.error('Failed to check OASIS errors:', err);
        if (!cancelled) setLockedOasisCheckFailed(true);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [needsLockedOasisCheck, visitId, savedVisitData]);

  // Hold Sign, Approve and Bypass until the chart's first scrub is in: the
  // editable form's Live Scrubber, or the locked-chart check above. Only the
  // first — later re-runs while editing keep the last count, and the backend
  // checks again on submit.
  const oasisCheckPending =
    Boolean(visit?.is_cms_submittable) &&
    !isMissedVisit &&
    (!isReadonly || needsLockedOasisCheck) &&
    oasisFatalCount === null &&
    !lockedOasisCheckFailed;

  const signButtonLabel = getSignButtonLabel(signTargetStatus);

  const editButtonLabel = useMemo(() => {
    if (!visit) return 'Unlock & Edit';
    // Reopening a finished chart is a distinct, more deliberate action.
    if (visit.status === 'completed' || visit.status === 'exported')
      return 'Reopen';
    if (
      visit.status === 'submitted_with_signature' &&
      hasClinicianRole &&
      !hasCaseManagerRole
    )
      return 'Unsubmit & Edit';
    return 'Unlock & Edit';
  }, [visit?.status, hasClinicianRole, hasCaseManagerRole]);

  // --- Data fetching ---

  // The save endpoint rejects a request whose timestamp doesn't match the stored
  // row, so back-to-back autosaves must read the newest value rather than the one
  // captured in the closure of the render that started the save.
  const visitDataTimestampRef = useRef<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!visitId) return;

      try {
        setLoading(true);
        setError(null);

        // Only the initial load is widened. Every other refresh in this file
        // follows a mutation (sign, approve, bypass, break signatures), and
        // none of those can run on a deleted chart, so they stay narrow.
        const [visitData, signaturesData] = await Promise.all([
          getVisit({
            path: { visit_id: visitId },
            ...(isDeleted && { query: { include_deleted: true } }),
          }),
          getTaskSignatures({
            path: { task_id: visitId },
            ...(isDeleted && { query: { include_deleted: true } }),
          }),
        ]);

        if (visitData?.data) {
          setVisit(visitData.data);
          visitDataTimestampRef.current =
            visitData.data.visit_data_last_updated ?? null;
          setTaskNotes(visitData.data.notes ?? '');
          onVisitUpdated?.(visitData.data);
        }
        if (signaturesData?.data) {
          setSignatures(signaturesData.data);
        }
      } catch (err) {
        console.error('Error fetching visit data:', err);
        setError('Failed to load visit details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [visitId, onVisitUpdated, isDeleted]);

  // Only charts that go to CMS: an OASIS-shaped chart that is never exported
  // (Skilled Nursing Assessment) is not held to the CMS scrubber.
  const hasOasisFatals =
    Boolean(visit?.is_cms_submittable) &&
    !isMissedVisit &&
    (oasisFatalCount ?? 0) > 0;

  // Workflow actions (sign, approve, miss, change-type, notes, print, Actions menu)
  const actions = useVisitDetailActions({
    visitId,
    visit,
    isDeleted,
    isEditable,
    isMissedVisit,
    canBypassSignature,
    canSubmitWithoutSignature,
    canReturnToClinician,
    canViewWorkLog,
    canViewEvv,
    canWaiveEvalOrder,
    hasStagedAnswers,
    taskNotes,
    setTaskNotes,
    setVisit,
    setSignatures,
    setSaveError,
    onVisitUpdated,
    attestations,
    // The backend enforces the same rule; this only spares the signer the
    // password prompt when the answer is already known to be no.
    blockSign:
      hasOasisFatals &&
      (signRequiresESignature
        ? oasisSigningRequirements.clinician
        : oasisSigningRequirements.qa),
    blockApprove: hasOasisFatals && oasisSigningRequirements.qa,
    hasOasisFatals,
    oasisFatalCount,
    oasisCheckPending,
  });

  const { oasisWarning } = actions;

  // --- OASIS form save (with signature invalidation dialog) ---

  const performSave = async (data: Record<string, unknown>) => {
    if (!visit || !visitId) return;

    setSaveError(null);

    const updatedVisit = await updateVisitData({
      path: { visit_id: visitId },
      body: {
        visit_data: data,
        visit_data_last_updated: visitDataTimestampRef.current,
      },
    });

    if (updatedVisit?.error) {
      const detailMessage = getUserFacingApiMessage(
        updatedVisit,
        'Failed to save visit data'
      );
      const isReadOnlyError = detailMessage.toLowerCase().includes('read-only');
      const isStaleError =
        getHttpStatus(updatedVisit) === 409 && !isReadOnlyError;

      if (isReadOnlyError) {
        showError(
          'Read-Only',
          'This visit is now in a read-only state. The page will reload to reflect the latest changes.'
        );
        setTimeout(() => window.location.reload(), 3000);
      } else if (isStaleError) {
        // Someone (or a reschedule) changed this chart since it was loaded.
        // Reloading is the only safe move — saving again would overwrite them.
        showError(
          'Out of Date',
          'This visit was changed elsewhere. The page will reload to show the latest version.'
        );
        setTimeout(() => window.location.reload(), 3000);
      }

      throw new Error('Failed to save visit data');
    }

    if (updatedVisit?.data) {
      setVisit(updatedVisit.data);
      visitDataTimestampRef.current =
        updatedVisit.data.visit_data_last_updated ?? null;
      onVisitUpdated?.(updatedVisit.data);
    }

    setSignatures([]);
  };

  const handleOASISFormSave = async (formSaveData: OASISFormData) => {
    if (!visit || !visitId) return;

    if (signatures.length > 0) {
      setPendingSaveData({ data: formSaveData, isFormSave: true });
      setShowConfirmDialog(true);

      return new Promise<void>((resolve, reject) => {
        savePromiseRef.current = { resolve, reject };
      });
    }

    try {
      await performSave(formSaveData);
    } catch (err) {
      console.error('Error saving OASIS form data:', err);
      setSaveError('Failed to save form data. Please try again.');
      throw err;
    }
  };

  // Stable identities so the memoized OASISForm skips re-rendering its large
  // question tree when this panel re-renders for unrelated state
  const oasisFormSaveRef = useRef(handleOASISFormSave);
  useEffect(() => {
    oasisFormSaveRef.current = handleOASISFormSave;
  });
  const handleOASISFormSaveStable = useCallback(
    (data: OASISFormData) => oasisFormSaveRef.current(data),
    []
  );
  const handleOasisSaveStatusChange = useCallback(
    (status: AutoSaveStatus, changeToken: number) => {
      setAutoSaveStatus(status);
      setAutoSaveChangeToken(changeToken);
    },
    []
  );
  // A medication order created from the embedded meds module is a new task; open it.
  const handleMedicationOrderCreated = useCallback(
    (order: OrderResponse) => {
      navigate(`/patients/${patientId}/visits/${order.id}`);
    },
    [navigate, patientId]
  );
  const handleMissedDirtyChange = useCallback(
    (dirty: boolean, canSave: boolean) => {
      if (dirty) {
        missedEditSeqRef.current += 1;
        if (canSave) {
          // Restart the pill's debounce countdown in step with the form's
          // autosave timer, which also resets on each edit.
          setAutoSaveChangeToken(t => t + 1);
          setAutoSaveStatus('unsaved');
        } else {
          // Dirty but not yet valid to autosave: a quiet "Not saved" rather
          // than a countdown that never fires. The inline field hints say why.
          // Set it even mid-save: any in-flight save is of the prior valid
          // payload, and once it resolves the form is still dirty+invalid, so
          // "Not saved" is the honest end state (avoids a stuck "Saving...").
          setAutoSaveStatus('blocked');
        }
      } else {
        // The form matches its saved data again, e.g. an edit was reverted
        // by hand or a save completed; latched outcomes stay visible
        setAutoSaveStatus(s =>
          s === 'unsaved' || s === 'blocked' ? 'idle' : s
        );
      }
    },
    []
  );

  const handleConfirmSave = async () => {
    if (!pendingSaveData) return;

    setShowConfirmDialog(false);
    setInvalidationLoading(true);
    try {
      await performSave(pendingSaveData.data);
      savePromiseRef.current?.resolve();
    } catch (err) {
      console.error('Error saving after confirmation:', err);
      savePromiseRef.current?.reject(err);
    } finally {
      setInvalidationLoading(false);
      setPendingSaveData(null);
      savePromiseRef.current = null;
    }
  };

  const handleCancelSave = () => {
    setShowConfirmDialog(false);

    if (isUnlockAction) {
      setIsUnlockAction(false);
      return;
    }

    // The sentinel tells the form's save handler this is a decline, not a
    // failure, so it must not latch an error status
    savePromiseRef.current?.reject(new SaveDeclinedError());
    savePromiseRef.current = null;

    if (pendingSaveData?.isFormSave) {
      setFormKey(prev => prev + 1);
    }

    // The declined edits are discarded by the remount above, so the pill
    // returns to its resting state
    setAutoSaveStatus('idle');
    setPendingSaveData(null);
  };

  // --- Unlock from readonly (break signatures via invalidation dialog) ---

  const handleReturnToEditMode = () => {
    if (!visit) return;
    // Locked visits with no active signatures still need unlock — the backend
    // transitions status even when there is nothing left to invalidate.
    if (signatures.length === 0) {
      void handleUnlockConfirm();
      return;
    }
    setIsUnlockAction(true);
    setShowConfirmDialog(true);
  };

  const handleUnlockConfirm = async () => {
    if (!visit) return;
    setShowConfirmDialog(false);
    setInvalidationLoading(true);
    try {
      const result = await breakTaskSignatures({
        path: { task_id: visit.id },
      });
      if (result.error) {
        console.error('Error unlocking visit:', result.error);
        setSaveError('Failed to unlock visit. Please try again.');
        return;
      }
      const [visitData, signaturesData] = await Promise.all([
        getVisit({ path: { visit_id: visit.id } }),
        getTaskSignatures({ path: { task_id: visit.id } }),
      ]);
      if (visitData?.data) {
        setVisit(visitData.data);
        onVisitUpdated?.(visitData.data);
      }
      if (signaturesData?.data) {
        setSignatures(signaturesData.data);
      }
    } catch (err) {
      console.error('Error unlocking visit:', err);
      setSaveError('Failed to unlock visit. Please try again.');
    } finally {
      setInvalidationLoading(false);
      setIsUnlockAction(false);
    }
  };

  const handleUpdateMissedData = async (payload: MissedVisitDataUpdate) => {
    if (!visitId) return;
    const seqAtStart = missedEditSeqRef.current;
    try {
      setMissedDataSaving(true);
      setSaveError(null);
      setAutoSaveStatus('saving');
      const result = await updateMissedVisitData({
        path: { visit_id: visitId },
        body: payload,
      });
      if (result.error) {
        console.error('Error updating missed visit data:', result.error);
        showError('Unable to save missed visit details. Please try again.');
        setAutoSaveStatus('error');
        return;
      }
      if (result.data) {
        setVisit(result.data);
        onVisitUpdated?.(result.data);
      }
      // Keep showing "unsaved" if the user edited again while this save ran
      if (missedEditSeqRef.current === seqAtStart) {
        setAutoSaveStatus('saved');
      }
    } catch (err) {
      console.error('Error updating missed visit data:', err);
      showError('Unable to save missed visit details. Please try again.');
      setAutoSaveStatus('error');
    } finally {
      setMissedDataSaving(false);
    }
  };

  // --- Render ---

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4' />
          <p className='text-muted-foreground'>Loading visit details...</p>
        </div>
      </div>
    );
  }

  if (error || !visit) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <AlertTriangle className='h-12 w-12 text-destructive mx-auto mb-4' />
          <h3 className='text-lg font-semibold mb-2'>Error Loading Visit</h3>
          <p className='text-muted-foreground mb-4'>
            {error || 'Visit not found'}
          </p>
          <Button
            onClick={() =>
              onBack ? onBack() : navigate(`/patients/${patientId}`)
            }
            variant='outline'
          >
            <ArrowLeft className='h-4 w-4' />
            Back to Patient
          </Button>
        </div>
      </div>
    );
  }

  // Delegate to OrderDetailPanel for order visit types
  if (isOrderType(visit.visit_type)) {
    return (
      <OrderDetailPanel
        orderId={visit.id}
        patientId={patientId}
        onBack={onBack}
        // The order panel refetches from scratch, so it has to be told the row
        // is deleted or its own load 404s where this one succeeded.
        isDeleted={isDeleted}
      />
    );
  }

  const hasPrimaryHeaderActions =
    (canBreakSignatures && !isReadonly) || canSign || canApprove;

  // OASIS has no inline-validation "Not saved": its only refused save is the
  // user cancelling the invalidation dialog, which the modal and the form
  // remount already communicate — so never surface 'blocked' for it (it would
  // otherwise race the cancel path's reset to a sticky "Not saved"). The missed
  // visit form does want it: a dirty-but-incomplete edit shows "Not saved"
  // rather than a save countdown that can't fire. And while the dialog waits
  // for a decision the parked onSave makes the form report "saving" though no
  // request is running, so stay quiet (idle) and let the modal own the moment
  // rather than showing a "Saving…" that isn't true.
  const pillStatus: AutoSaveStatus =
    (autoSaveStatus === 'blocked' && !isMissedVisit) ||
    (showConfirmDialog &&
      pendingSaveData?.isFormSave &&
      autoSaveStatus === 'saving')
      ? 'idle'
      : autoSaveStatus;

  return (
    <div className='flex flex-col h-full'>
      <VisitDetailHeader
        visit={visit}
        patientId={patientId}
        isMissedVisit={isMissedVisit}
        onBack={onBack}
        signatures={signatures}
        bypasses={signatureBypasses}
        hasPrimaryHeaderActions={hasPrimaryHeaderActions}
        canBreakSignatures={canBreakSignatures}
        canSign={canSign}
        canApprove={canApprove}
        isReadonly={isReadonly}
        editButtonLabel={editButtonLabel}
        signButtonLabel={signButtonLabel}
        editingLoading={actions.editingLoading}
        signingLoading={actions.signingLoading}
        approvingLoading={actions.approvingLoading}
        oasisCheckPending={oasisCheckPending}
        attestationsLoading={attestationsLoading}
        visitAttestation={actions.visitAttestation}
        hasStagedAnswers={hasStagedAnswers}
        stagedAnswersTooltip={stagedAnswersTooltip}
        onBreakSignatures={() => void actions.handleBreakSignatures()}
        onSign={actions.handleSignAndSubmit}
        onApprove={actions.handleApprove}
        secondaryActions={actions.secondaryHeaderActions}
      />

      {/* Save status pill: a sibling above the scrollable content div, so it
          stays in view in either scroll configuration — when the content div
          below is the scroller the pill sits outside it, and when the panel
          grows and the page scrolls (Layout's main) the sticky positioning
          pins it to the top. The row collapses while the pill is hidden so
          idle missed-visit forms don't get an empty strip. */}
      {!isReadonly && (
        <div
          className={`pointer-events-none sticky top-0 z-20 flex shrink-0 justify-end px-5 ${
            isMissedVisit && pillStatus === 'idle' ? '' : 'py-2'
          }`}
        >
          <AutoSavePill
            status={pillStatus}
            variant='autosave'
            debounceMs={
              isMissedVisit
                ? MISSED_VISIT_AUTOSAVE_DEBOUNCE_MS
                : getOasisAutosaveDebounceMs(signatures.length > 0)
            }
            changeToken={autoSaveChangeToken}
          />
        </div>
      )}

      {/* Main Content Area. The padding sits on an inner wrapper rather than on
          the scroll box itself: a scroll container's padding insets where its
          sticky descendants may pin, so padding here would leave a strip of that
          height above every pinned element for content to scroll through.

          Growing this padding past the gutter the visit form pins against
          (`PINNED_PANEL_GUTTER_PX` in OASISForm) makes the form's pinned section
          list clip its own header at the end of a long form. That list also
          parks at this padding (`top-5`), so change both together.

          Scrolls here only from `md` up. On mobile this stays overflow-visible
          so the page keeps scrolling and the headers above scroll away with it;
          an `overflow: auto` box would become the scrollport whether or not it
          ever scrolled, which is what strands sticky children inside it. */}
      <div className='flex-1 md:overflow-auto'>
        <div className='p-5 space-y-4'>
          {/* Imported-chart signpost (EMR-1264): say the visit came from
              Kinnser, then say only what is true of the chart below it — see
              `importedChartState`. */}
          {!importedCopy && closedEpisodeCopyForVisit && (
            <div
              className='bg-muted/40 border rounded-lg p-4 flex items-start gap-3'
              data-testid='closed-episode-banner'
            >
              <Lock
                className='h-5 w-5 text-muted-foreground shrink-0 mt-0.5'
                aria-hidden='true'
              />
              <div className='text-sm'>
                <p className='font-medium'>
                  {closedEpisodeCopyForVisit.headline}
                </p>
                <p className='text-muted-foreground'>
                  {closedEpisodeCopyForVisit.detail}
                </p>
              </div>
            </div>
          )}
          <SourceAssessmentEvidence
            snapshots={visit?.source_assessment_evidence}
          />
          {importedCopy && (
            <div className='bg-muted/40 border rounded-lg p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
              <div className='flex items-start gap-3'>
                <FileText
                  className='h-5 w-5 text-muted-foreground shrink-0 mt-0.5'
                  aria-hidden='true'
                />
                <div className='text-sm'>
                  <p className='font-medium'>{importedCopy.headline}</p>
                  <p className='text-muted-foreground'>{importedCopy.detail}</p>
                </div>
              </div>
              {kinnserPrintViewDocumentId && (
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => void handleOpenKinnserPrintView()}
                  disabled={printViewOpening}
                  className='shrink-0 self-start sm:self-auto'
                >
                  {printViewOpening
                    ? 'Opening…'
                    : 'View the External EMR print view'}
                </Button>
              )}
            </div>
          )}

          {saveError && (
            <div className='text-sm text-destructive bg-destructive/10 p-3 rounded-md'>
              {saveError}
            </div>
          )}

          {/* Signature Invalidation Warning (editable states with active signatures) */}
          {isEditable && signatures.length > 0 && (
            <div className='bg-amber-50 border border-amber-200 rounded-lg p-4'>
              <div className='flex items-start gap-3'>
                <AlertCircle className='h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0' />
                <div className='flex-1'>
                  <h4 className='font-semibold text-amber-900 mb-1'>
                    Signature Notice
                  </h4>
                  <p className='text-sm text-amber-800'>
                    This visit has been signed by {signatures.length}{' '}
                    {signatures.length === 1 ? 'person' : 'people'}. Editing the
                    visit data will invalidate all signatures and require
                    re-signing.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Visit Content: Readonly or Editable */}
          {isReadonly ? (
            <div className='space-y-4'>
              <div className='rounded-lg border bg-muted/40 p-3'>
                <div className='flex items-start justify-between gap-3'>
                  <div className='min-w-0'>
                    <p className='text-sm font-medium'>
                      {isDeleted
                        ? 'Deleted Visit (read-only)'
                        : closedEpisodeLocked
                          ? 'Closed Episode (read-only)'
                          : !canEditChart
                            ? 'Chart (read-only)'
                            : visit.status === 'completed'
                              ? 'Completed Chart (read-only)'
                              : visit.status === 'exported'
                                ? 'Exported Chart (read-only)'
                                : 'Signed Chart (read-only)'}
                    </p>
                    <p className='text-xs text-muted-foreground'>
                      {isDeleted
                        ? 'This visit was deleted. Its chart stays readable and printable, and cannot be edited.'
                        : closedEpisodeLocked
                          ? 'This certification period ended and was closed. The chart stays readable and printable.'
                          : !canEditChart
                            ? 'You do not have permission to edit this chart.'
                            : visit.status === 'completed'
                              ? 'This visit has been approved and completed.'
                              : visit.status === 'exported'
                                ? 'This visit has been exported.'
                                : 'This visit is locked. Select Edit to remove signatures and return to the editable form.'}
                    </p>
                  </div>
                  {canBreakSignatures && !closedEpisodeLocked && (
                    <Button
                      size='default'
                      variant='default'
                      onClick={handleReturnToEditMode}
                      disabled={invalidationLoading}
                      className='flex items-center gap-1.5 shrink-0 font-medium shadow-sm'
                    >
                      <LockOpen className='h-4 w-4' />
                      {editButtonLabel}
                    </Button>
                  )}
                </div>
              </div>
              {isMissedVisit ? (
                <MissedVisitForm
                  key={visit.missed_visit_data?.id ?? 'readonly'}
                  data={visit.missed_visit_data}
                  isReadonly
                  isSaving={false}
                  onUpdate={handleUpdateMissedData}
                  physicians={physicians}
                />
              ) : (
                <div className='h-[calc(100vh-14rem)] min-h-[760px]'>
                  <ReadonlyChartView
                    visitId={visitId}
                    includeDeleted={isDeleted}
                  />
                </div>
              )}
            </div>
          ) : isMissedVisit ? (
            <MissedVisitForm
              key={visit.missed_visit_data?.id ?? 'edit'}
              data={visit.missed_visit_data}
              isReadonly={false}
              isSaving={missedDataSaving}
              autosave
              saveErrored={autoSaveStatus === 'error'}
              onUpdate={handleUpdateMissedData}
              physicians={physicians}
              onDirtyChange={handleMissedDirtyChange}
            />
          ) : (
            // Question-grid min-width lives on the grid itself inside OASISForm
            // (overflow-x-auto + md:min-w-[64rem]), not around the whole chart
            // body — so Goals / Meds / Wounds / Diagnoses stay fluid on iPad
            // while the dense 12-column fields keep their floor.
            <OASISForm
              key={`${visitId}-${formKey}`}
              visitId={visitId}
              visitType={visit.visit_type || 'SOC'}
              isOasis={visit.is_oasis}
              initialData={visit.visit_data as OASISFormData}
              onSave={handleOASISFormSaveStable}
              hasActiveSignatures={signatures.length > 0}
              showInlineValidation
              onSaveStatusChange={handleOasisSaveStatusChange}
              patientId={patientId}
              patient={patient}
              visitStatus={visit.status}
              episodeClosed={visit.episode_closed}
              episodeId={visit.episode_id}
              episodeStartDate={visit.episode_start_date}
              episodeEndDate={visit.episode_end_date}
              onMedicationOrderCreated={handleMedicationOrderCreated}
              // Answer carryover is limited to the routine-visit forms that
              // have a copy policy (EMR-675 RN, EMR-1008 PT): every source visit
              // must share this visit's form schema. Offered only where a
              // visit-data write is allowed — an editable status, and no active
              // signature a bulk copy would invalidate. The backend rejects on
              // the same conditions.
              carryoverEnabled={
                supportsAnswerCarryover(visit) &&
                isEditable &&
                signatures.length === 0
              }
              onCarryoverPendingChange={setHasStagedAnswers}
              // The patient's clinic stands in for a visit that carries no
              // clinic of its own: both name an AgencyClinic, and a patient
              // is admitted to the clinic whose physicians the picker offers.
              // Without the fallback such a visit loses the picker entirely.
              agencyClinicId={visit.agency_clinic_id ?? patient?.clinic_id}
              onFatalCountChange={setOasisFatalCount}
              scrubberFocusRequest={scrubberFocusRequest}
            />
          )}
        </div>
      </div>

      <SignatureHistoryDialog
        open={actions.historyOpen}
        onOpenChange={actions.setHistoryOpen}
        taskId={visit.id}
        includeDeleted={isDeleted}
      />

      <WorkLogDialog
        open={actions.workLogOpen}
        onOpenChange={actions.setWorkLogOpen}
        taskId={visit.id}
        taskType='visit'
        includeDeleted={isDeleted}
      />

      <EvvReadingsDialog
        open={actions.evvOpen}
        onOpenChange={actions.setEvvOpen}
        visitId={visit.id}
        canRemove={canRemoveEvv}
        onRemoved={actions.refreshEvvIndicator}
      />

      <VisitSigningDialogs
        showConfirmDialog={showConfirmDialog}
        isUnlockAction={isUnlockAction}
        signatures={signatures}
        invalidationLoading={invalidationLoading}
        editButtonLabel={editButtonLabel}
        onConfirmSave={() => void handleConfirmSave()}
        onUnlockConfirm={() => void handleUnlockConfirm()}
        onCancelSave={handleCancelSave}
        signAttestationOpen={actions.signAttestationOpen}
        signRequiresESignature={signRequiresESignature}
        visitAttestation={actions.visitAttestation}
        signerName={user?.name ?? null}
        signerEmail={user?.email ?? null}
        signingLoading={actions.signingLoading}
        signButtonLabel={signButtonLabel}
        patientId={patientId}
        onSignConfirm={actions.doSignAndSubmit}
        onSignCancel={() => actions.setSignAttestationOpen(false)}
        approveAttestationOpen={actions.approveAttestationOpen}
        approvingLoading={actions.approvingLoading}
        onApproveConfirm={actions.doApprove}
        onApproveCancel={() => actions.setApproveAttestationOpen(false)}
        bypassDialog={actions.bypassDialog}
      />
      <OasisFatalErrorsDialog
        open={actions.oasisBlockedCount !== undefined}
        fatalCount={actions.oasisBlockedCount ?? null}
        locked={isReadonly}
        canFix={!isReadonly || (canBreakSignatures && !closedEpisodeLocked)}
        onClose={() => actions.setOasisBlockedCount(undefined)}
        onProceed={
          oasisWarning
            ? () => {
                actions.setOasisBlockedCount(undefined);
                oasisWarning.proceed();
              }
            : undefined
        }
        proceedLabel={oasisWarning?.proceedLabel}
        onFix={() => {
          actions.setOasisBlockedCount(undefined);
          // A locked chart shows no Live Scrubber; unlocking returns it to the
          // editable form, where the scrubber is.
          if (isReadonly) handleReturnToEditMode();
          else showOasisErrors();
        }}
      />

      <VisitTaskNotesDialog
        open={actions.showNotesDialog}
        onOpenChange={actions.setShowNotesDialog}
        draftNotes={actions.draftNotes}
        onDraftNotesChange={actions.setDraftNotes}
        isDeleted={isDeleted}
        episodeId={visit.episode_id}
        saveError={saveError}
        isSaving={actions.taskNotesLoading}
        onSave={() => void actions.handleSaveNotes()}
      />

      <ConfirmationDialog
        open={actions.confirmWaiveOrderOpen}
        title={WAIVE_EVAL_ORDER_CONFIRM.title}
        message={WAIVE_EVAL_ORDER_CONFIRM.message}
        confirmLabel={WAIVE_EVAL_ORDER_CONFIRM.confirmLabel}
        loading={actions.orderNotRequiredLoading}
        onAccept={() => void actions.handleToggleOrderNotRequired()}
        onCancel={() => actions.setConfirmWaiveOrderOpen(false)}
      />

      <MarkVisitMissedDialog
        open={actions.showMarkMissedDialog}
        onOpenChange={actions.setShowMarkMissedDialog}
        isSaving={actions.markMissedLoading}
        physicians={physicians}
        onSave={actions.handleMarkAsMissed}
      />

      <ChangeVisitTypeDialog
        open={actions.showChangeTypeDialog}
        onOpenChange={actions.setShowChangeTypeDialog}
        options={actions.visitTypeOptions}
        selectedVisitType={actions.selectedVisitType}
        onSelectedVisitTypeChange={actions.setSelectedVisitType}
        isLoading={actions.changeTypeLoading}
        onConfirm={() => void actions.handleChangeVisitType()}
      />

      <VisitTransitionNotesDialog
        open={actions.showTransitionNotesModal}
        onOpenChange={open => {
          if (!open) actions.handleCancelTransition();
        }}
        notes={actions.transitionNotes}
        onNotesChange={actions.setTransitionNotes}
        onCancel={actions.handleCancelTransition}
        onContinue={() => void actions.handleConfirmTransition()}
      />
    </div>
  );
};
